import { useEffect } from 'react';

import { fg } from '@confluence/feature-gating';

import { useOpenPreviewPanel } from './useOpenPreviewPanel.tsx';

const ALLOWED_URL_PROTOCOLS = new Set(['http:', 'https:']);

// Legitimate senders are same-origin, or iframes Confluence rendered itself (e.g. a Jira
// preview panel on a custom domain). A page that frames or opens Confluence is neither.
const isAllowedSender = (event: MessageEvent): boolean => {
	if (event.origin === window.location.origin) {
		return true;
	}
	if (event.source && typeof document !== 'undefined') {
		return Array.from(document.querySelectorAll('iframe')).some(
			(iframe) => iframe.contentWindow === event.source,
		);
	}
	return false;
};

// The url ends up in an iframe src; a javascript: scheme there executes in this origin.
const isAllowedUrl = (url: unknown): boolean => {
	if (typeof url !== 'string') {
		return false;
	}
	try {
		return ALLOWED_URL_PROTOCOLS.has(new URL(url, window.location.origin).protocol);
	} catch {
		return false;
	}
};

export const PreviewPanelsPostMessageListener = () => {
	const { open } = useOpenPreviewPanel();

	// Listen for messages from iframes to open preview panels
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			// Only handle messages with the expected type
			if (event.data?.type !== 'OPEN_PREVIEW_PANEL') {
				return;
			}

			if (!isAllowedSender(event) && fg('cc_preview_panel_validate_message_source')) {
				return;
			}

			const { payload } = event.data;

			if (!payload?.url) {
				return;
			}

			if (!isAllowedUrl(payload.url) && fg('cc_preview_panel_validate_message_source')) {
				return;
			}

			open(payload);
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [open]);

	return null;
};
